import React, {useEffect, useState} from "react";
import {
  AButton,
  AButtonGroup,
  APagination,
  APanel,
  APanelBody,
  APanelFooter,
  APanelHeader,
  ASelect
} from "@cisco-sbg-ui/magna-react";
import {t} from "i18next";
import {useDispatch, useSelector} from "react-redux";
import AuditLogPanel from "features/incidentDetail/WorkLogTab/AuditLogPanel";
import NotesPanel from "features/incidentDetail/WorkLogTab/NotesPanel";
import {
  getIncidentAuditLog,
  getIncidentNotes
} from "store/features/incidentDetailSlice";
import {
  selectAuditLogTotalCount,
  selectIncidentDetailShortId,
  selectIncidentNotesTotalCount
} from "store/selectors";
import {sendNotesAuditLogToggleTelemetry} from "api/telemetry/incidentsTelemetrics";

export const API_NEWEST_SORT = `desc`;
export const API_OLDEST_SORT = `asc`;

const IncidentDetailWorklog = () => {
  const tWorklogTab = "incidentDetail.worklogTab";

  const AUDIT_LOG = t(`${tWorklogTab}.auditLogTab`);
  const NOTES = t(`${tWorklogTab}.notesTab`);
  const ADD_NOTE = t(`notes.addNote`);
  const NEWEST_SORT = t(`${tWorklogTab}.sortByNewest`);
  const OLDEST_SORT = t(`${tWorklogTab}.sortByOldest`);
  const sortOptions = [
    {
      id: API_NEWEST_SORT,
      name: NEWEST_SORT
    },
    {
      id: API_OLDEST_SORT,
      name: OLDEST_SORT
    }
  ];
  const sortMap = {
    [API_NEWEST_SORT]: NEWEST_SORT,
    [API_OLDEST_SORT]: OLDEST_SORT
  };

  const [selectedPanelBody, setSelectedPanelBody] = useState([NOTES]);
  const [addNoteEditorOpen, setAddNoteEditorOpen] = useState(false);

  const [auditLogSort, setAuditLogSort] = useState(API_NEWEST_SORT);
  const [auditLogPage, setAuditLogPage] = useState(1);
  const [auditLogResultsPerPage, setAuditLogResultsPerPage] = useState(10);

  const [notesSort, setNotesSort] = useState(API_NEWEST_SORT);
  const [notesPage, setNotesPage] = useState(1);
  const [notesResultsPerPage, setNotesResultsPerPage] = useState(10);

  const isNotesActive = selectedPanelBody.includes(NOTES);
  const shortId = useSelector(selectIncidentDetailShortId);
  const auditLogTotalCount = useSelector(selectAuditLogTotalCount);
  const notesTotalCount = useSelector(selectIncidentNotesTotalCount);
  const dispatch = useDispatch();

  const auditLogPaginationState = {
    page: auditLogPage,
    resultsPerPage: auditLogResultsPerPage,
    sort: auditLogSort,
    totalCount: auditLogTotalCount
  };

  const notesPaginationState = {
    page: notesPage,
    resultsPerPage: notesResultsPerPage,
    sort: notesSort,
    totalCount: notesTotalCount
  };

  const pagination = isNotesActive
    ? notesPaginationState
    : auditLogPaginationState;

  const panelBodyMap = {
    [NOTES]: (
      <NotesPanel
        limit={notesResultsPerPage}
        sortBy={notesSort}
        isEditorOpen={addNoteEditorOpen}
        handleEditorOpen={setAddNoteEditorOpen}
      />
    ),
    [AUDIT_LOG]: <AuditLogPanel sortBy={auditLogSort} />
  };

  useEffect(() => {
    dispatch(
      getIncidentNotes({
        shortId,
        sortBy: notesSort,
        currentPage: notesPage,
        resultsPerPage: notesResultsPerPage
      })
    );
    /**
     * @todo Remove `notesSort` as a dependency and associate
     * the dispatch with the action within `ASelect`.
     */
  }, [dispatch, notesSort, notesPage, notesResultsPerPage]);

  useEffect(() => {
    dispatch(
      getIncidentAuditLog({
        shortId,
        sortBy: auditLogSort,
        currentPage: auditLogPage,
        resultsPerPage: auditLogResultsPerPage
      })
    );
  }, [dispatch, auditLogSort, auditLogPage, auditLogResultsPerPage]);

  return (
    <APanel data-testid={"IncidentDetailWorklog-Panel"}>
      <APanelHeader>
        <AButtonGroup
          multiple
          selectedValues={selectedPanelBody}
          onChange={(value) => {
            setSelectedPanelBody([value]);
            sendNotesAuditLogToggleTelemetry(
              value,
              shortId,
              notesTotalCount,
              auditLogTotalCount
            );
          }}>
          <AButton
            primary
            value={NOTES}
            medium
            data-testid={"IncidentDetailWorklog-Button-notes"}>
            {NOTES}
          </AButton>
          <AButton
            primary
            value={AUDIT_LOG}
            medium
            data-testid={"IncidentDetailWorklog-Button-audit-log"}>
            {AUDIT_LOG}
          </AButton>
        </AButtonGroup>
        {pagination.totalCount ? (
          <ASelect
            medium
            style={{maxWidth: "170px"}}
            items={sortOptions}
            itemText="name"
            itemValue="id"
            placeholder={
              isNotesActive ? sortMap[notesSort] : sortMap[auditLogSort]
            }
            onSelected={(sort) =>
              isNotesActive ? setNotesSort(sort.id) : setAuditLogSort(sort.id)
            }
            key={isNotesActive ? sortMap[notesSort] : sortMap[auditLogSort]}
            data-testid={"IncidentDetailWorklog-Select"}
          />
        ) : null}
        <AButton
          className="ml-2"
          secondary
          medium
          value={ADD_NOTE}
          style={{minWidth: "fit-content"}}
          onClick={() => {
            setSelectedPanelBody([NOTES]);
            setAddNoteEditorOpen(true);
          }}
          data-testid={"IncidentDetailWorklog-Button-add-note"}>
          {ADD_NOTE}
        </AButton>
      </APanelHeader>
      <APanelBody data-testid={"IncidentDetailWorklog-PanelBody"}>
        {panelBodyMap[selectedPanelBody]}
      </APanelBody>
      <APanelFooter>
        <div className="d-flex justify-end">
          {pagination.totalCount ? (
            <APagination
              onPageChange={
                isNotesActive
                  ? (value) => setNotesPage(value)
                  : (value) => setAuditLogPage(value)
              }
              onResultsPerPageChange={
                isNotesActive
                  ? (value) => setNotesResultsPerPage(value)
                  : (value) => setAuditLogResultsPerPage(value)
              }
              page={pagination.page}
              resultsPerPage={pagination.resultsPerPage}
              total={pagination.totalCount}
              localeFormatting
              className="mt-2"
            />
          ) : null}
        </div>
      </APanelFooter>
    </APanel>
  );
};

export default IncidentDetailWorklog;
